const CONTEXT_STORAGE_KEY = 'SPECIAL_DISEASE_TRACK_CONTEXT'
const EXPOSURE_STORAGE_KEY = 'SPECIAL_DISEASE_DOCTOR_EXPOSURES'
const SEARCH_RESULT_REPORT_STORAGE_KEY = 'SPECIAL_DISEASE_SEARCH_RESULT_REPORTS'
const PENDING_PAYMENT_STORAGE_KEY = 'SPECIAL_DISEASE_PENDING_PAYMENT'
const JOURNEY_TTL = 30 * 60 * 1000
const MAX_EXPOSURE_KEYS = 300
const MAX_SEARCH_RESULT_REPORT_KEYS = 300
const PAGE_VERSION = 'tracking_v1'
const SEARCH_SELECTION_RESET = {
  special_disease_doctor_id: '',
  special_disease_doctor_name: '',
  special_disease_doctor_origin: '',
  special_disease_hospital_area_id: '',
  special_disease_hospital_area_name: '',
  special_disease_department_id: '',
  special_disease_department_name: '',
  special_disease_sub_department_name: ''
}

function now () {
  return Date.now()
}

function createId (prefix) {
  let random = ''
  try {
    const values = new Uint32Array(2)
    window.crypto.getRandomValues(values)
    random = Array.from(values).map(value => value.toString(36)).join('')
  } catch (error) {
    random = Math.random().toString(36).slice(2, 14)
  }
  return `${prefix}_${now().toString(36)}_${random}`
}

function readJson (storage, key) {
  try {
    const value = storage.getItem(key)
    return value ? JSON.parse(value) : null
  } catch (error) {
    return null
  }
}

function writeJson (storage, key, value) {
  try {
    storage.setItem(key, JSON.stringify(value))
  } catch (error) {}
}

function removeItem (storage, key) {
  try {
    storage.removeItem(key)
  } catch (error) {}
}

function sanitizeKeyword (keyword) {
  return String(keyword || '')
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/1\d{10}/g, '1**********')
    .replace(/\d{17}[\dXx]/g, '******************')
    .slice(0, 100)
}

function sanitizeText (text, maxLength = 120) {
  return String(text || '')
    .replace(/1\d{10}/g, '1**********')
    .replace(/\d{17}[\dXx]/g, '******************')
    .slice(0, maxLength)
}

function isIdProperty (key) {
  return /(^|_)(id|ids)$/.test(key)
}

function normalizeTrackingProperties (data = {}) {
  return Object.keys(data || {}).reduce((properties, key) => {
    let value = data[key]
    if (value === undefined || value === null || value === '') return properties
    if (key === 'keyword' || key === 'normalized_keyword' || key === 'special_disease_keyword') {
      value = sanitizeKeyword(value)
    } else if (key === 'error_message') {
      value = sanitizeText(value)
    } else if (isIdProperty(key)) {
      value = Array.isArray(value) ? value.map(String).join(',') : String(value)
    }
    properties[key] = value
    return properties
  }, {})
}

function resolveEntrySource (route = {}) {
  const query = route.query || {}
  const source = String(
    query.entry_source ||
    query.from_channel ||
    query.source ||
    'direct'
  ).trim().slice(0, 80)
  return source && !/^\d+$/.test(source) ? source : 'direct'
}

function readContext () {
  const context = readJson(window.sessionStorage, CONTEXT_STORAGE_KEY)
  if (!context || !context.special_disease_journey_id) return null
  if (now() - Number(context.last_active_at || 0) > JOURNEY_TTL) {
    removeItem(window.sessionStorage, CONTEXT_STORAGE_KEY)
    removeItem(window.sessionStorage, EXPOSURE_STORAGE_KEY)
    removeItem(window.sessionStorage, SEARCH_RESULT_REPORT_STORAGE_KEY)
    return null
  }
  if (/^\d+$/.test(String(context.special_disease_entry_source || ''))) {
    context.special_disease_entry_source = 'direct'
  }
  return context
}

function writeContext (context) {
  const nextContext = {
    ...context,
    source_biz: 'special_disease',
    special_disease_page_version: context.special_disease_page_version || PAGE_VERSION,
    last_active_at: now()
  }
  writeJson(window.sessionStorage, CONTEXT_STORAGE_KEY, nextContext)
  return nextContext
}

export function ensureSpecialDiseaseJourney ({ route, entrySource, pageName, pagePath } = {}) {
  const query = route?.query || {}
  const routeJourneyId = query.special_disease_journey_id || query.sd_journey_id
  const routeSearchId = query.special_disease_search_id || query.sd_search_id
  let context = readContext()

  if (!context) {
    context = {
      special_disease_journey_id: routeJourneyId || createId('sdj'),
      special_disease_search_id: routeSearchId || '',
      special_disease_entry_source: entrySource || resolveEntrySource(route),
      special_disease_page_version: PAGE_VERSION,
      created_at: now()
    }
  }

  return writeContext({
    ...context,
    ...(routeJourneyId ? { special_disease_journey_id: routeJourneyId } : {}),
    ...(routeSearchId ? { special_disease_search_id: routeSearchId } : {}),
    ...(entrySource ? { special_disease_entry_source: entrySource } : {}),
    ...(pageName ? { special_disease_page_name: pageName } : {}),
    ...(pagePath ? { special_disease_page_path: pagePath } : {})
  })
}

export function updateSpecialDiseaseTrackingContext (data = {}) {
  const context = readContext()
  if (!context) return null
  const nextData = { ...data }
  if (Object.prototype.hasOwnProperty.call(nextData, 'special_disease_keyword')) {
    nextData.special_disease_keyword = sanitizeKeyword(nextData.special_disease_keyword)
  }
  return writeContext({ ...context, ...nextData })
}

export function startSpecialDiseaseSearch ({ keyword, keywordSource, extra = {} } = {}) {
  const context = ensureSpecialDiseaseJourney()
  return writeContext({
    ...context,
    ...SEARCH_SELECTION_RESET,
    ...extra,
    special_disease_search_id: createId('sds'),
    special_disease_keyword: sanitizeKeyword(keyword),
    special_disease_keyword_source: keywordSource || 'unknown'
  })
}

export function ensureSpecialDiseaseSearch ({ keyword, keywordSource } = {}) {
  const safeKeyword = sanitizeKeyword(keyword)
  const context = ensureSpecialDiseaseJourney()
  if (context.special_disease_search_id && context.special_disease_keyword === safeKeyword) {
    return context
  }
  return writeContext({
    ...context,
    ...SEARCH_SELECTION_RESET,
    special_disease_search_id: createId('sds'),
    special_disease_keyword: safeKeyword,
    special_disease_keyword_source: keywordSource || 'direct_route'
  })
}

export function getSpecialDiseaseTrackingContext () {
  const context = readContext()
  if (!context) return null
  const {
    last_active_at,
    created_at,
    ...trackingProperties
  } = context
  return trackingProperties
}

export function getSpecialDiseaseTrackingRouteQuery (context = getSpecialDiseaseTrackingContext()) {
  if (!context) return {}
  return {
    ...(context.special_disease_journey_id ? { sd_journey_id: context.special_disease_journey_id } : {}),
    ...(context.special_disease_search_id ? { sd_search_id: context.special_disease_search_id } : {})
  }
}

function getSearchResultReportKeys () {
  // Mini-program routes can run in separate webviews. sessionStorage is scoped
  // to a single webview, while localStorage is shared by same-origin pages.
  const keys = readJson(window.localStorage, SEARCH_RESULT_REPORT_STORAGE_KEY)
  return Array.isArray(keys) ? keys : []
}

export function shouldTrackSpecialDiseaseSearchResult (searchId, status = 'success') {
  const normalizedSearchId = String(searchId || '').trim()
  if (!normalizedSearchId) return true
  const key = `${normalizedSearchId}|${status}`
  const keys = getSearchResultReportKeys()
  if (keys.includes(key)) return false
  keys.push(key)
  writeJson(window.localStorage, SEARCH_RESULT_REPORT_STORAGE_KEY, keys.slice(-MAX_SEARCH_RESULT_REPORT_KEYS))
  return true
}

function shouldAttachContext (eventName) {
  return /^EV_SPECIAL_DISEASE_/.test(eventName) ||
    /^EV_DD_/.test(eventName) ||
    /^EV_OC_/.test(eventName) ||
    /^EV_OP_/.test(eventName) ||
    /^EV_REG_/.test(eventName)
}

export function enrichSpecialDiseaseTrackData (eventName, data = {}) {
  const normalizedEventName = String(eventName || '')
  const isSpecialDiseaseEvent = /^EV_SPECIAL_DISEASE_/.test(normalizedEventName)
  if (!shouldAttachContext(normalizedEventName)) return data || {}
  const context = getSpecialDiseaseTrackingContext()
  if (!context) return isSpecialDiseaseEvent ? normalizeTrackingProperties(data) : data || {}
  if (!isSpecialDiseaseEvent && !context.special_disease_doctor_id) return data || {}
  writeContext({ ...readContext() })
  return normalizeTrackingProperties({
    ...context,
    ...(data || {})
  })
}

export function trackSpecialDiseaseEvent (eventName, data = {}) {
  if (!readContext() || typeof window.track_try_catch !== 'function') return
  window.track_try_catch(eventName, data)
}

function getExposureKeys () {
  const keys = readJson(window.sessionStorage, EXPOSURE_STORAGE_KEY)
  return Array.isArray(keys) ? keys : []
}

export function trackSpecialDiseaseDoctorExposure (data = {}) {
  const context = readContext()
  if (!context) return
  const key = [
    context.special_disease_journey_id,
    context.special_disease_search_id || 'no_search',
    data.event_origin || context.special_disease_page_name || 'unknown',
    data.doctor_id || data.doctor_name || 'unknown'
  ].join('|')
  const exposureKeys = getExposureKeys()
  if (exposureKeys.includes(key)) return
  exposureKeys.push(key)
  writeJson(window.sessionStorage, EXPOSURE_STORAGE_KEY, exposureKeys.slice(-MAX_EXPOSURE_KEYS))
  trackSpecialDiseaseEvent('EV_SPECIAL_DISEASE_DOCTOR_CARD_SHOW', data)
}

export const specialDiseaseExposure = {
  inserted (el, binding) {
    if (typeof window.IntersectionObserver !== 'function') {
      const fallbackTimer = window.setTimeout(() => {
        if (typeof binding.value === 'function') binding.value()
      }, 1000)
      el.__specialDiseaseExposureCleanup__ = () => window.clearTimeout(fallbackTimer)
      return
    }
    let timer = null
    let reported = false
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (reported) return
        if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
          if (timer) return
          timer = window.setTimeout(() => {
            reported = true
            timer = null
            if (typeof binding.value === 'function') binding.value()
            observer.disconnect()
          }, 1000)
        } else if (timer) {
          window.clearTimeout(timer)
          timer = null
        }
      })
    }, { threshold: [0.5] })
    observer.observe(el)
    el.__specialDiseaseExposureCleanup__ = () => {
      if (timer) window.clearTimeout(timer)
      observer.disconnect()
    }
  },
  unbind (el) {
    if (el.__specialDiseaseExposureCleanup__) {
      el.__specialDiseaseExposureCleanup__()
      delete el.__specialDiseaseExposureCleanup__
    }
  }
}

export function getSpecialDiseaseTrackError (error) {
  const responseData = error?.response?.data || {}
  return {
    error_code: String(responseData.code || responseData.status || error?.code || 'unknown').slice(0, 80),
    error_type: error?.response ? 'api_error' : 'network_error',
    error_message: String(responseData.message || error?.message || 'unknown').slice(0, 120)
  }
}

export function markSpecialDiseasePaymentStarted ({ orderId, orderNo, paymentChannel, amount } = {}) {
  const context = readContext()
  if (!context) return
  writeJson(window.localStorage, PENDING_PAYMENT_STORAGE_KEY, {
    order_id: String(orderId || ''),
    order_no: String(orderNo || ''),
    payment_channel: paymentChannel || 'unknown',
    amount: Number(amount || 0),
    created_at: now(),
    context: getSpecialDiseaseTrackingContext()
  })
}

export function trackSpecialDiseasePaymentResult (order = {}) {
  const pending = readJson(window.localStorage, PENDING_PAYMENT_STORAGE_KEY)
  if (!pending || pending.reported_result) return
  if (now() - Number(pending.created_at || 0) > JOURNEY_TTL) {
    removeItem(window.localStorage, PENDING_PAYMENT_STORAGE_KEY)
    return
  }

  const orderIdentifiers = [order.order_id, order.id, order.order_no].filter(Boolean).map(String)
  const pendingIdentifiers = [pending.order_id, pending.order_no].filter(Boolean).map(String)
  if (orderIdentifiers.length && pendingIdentifiers.length && !orderIdentifiers.some(id => pendingIdentifiers.includes(id))) return

  const status = Number(order.status)
  let result = ''
  let failureReason = ''
  if (status === 9 && Number(order.cancel_status || 0) === 0) {
    result = 'success'
  } else if (status === -1) {
    result = 'fail'
    failureReason = 'payment_timeout'
  } else if (status === -2) {
    result = 'fail'
    failureReason = 'payment_failed'
  } else {
    return
  }

  if (!readContext() && pending.context?.special_disease_journey_id) {
    writeContext({ ...pending.context, created_at: pending.created_at })
  }
  trackSpecialDiseaseEvent('EV_SPECIAL_DISEASE_PAY_RESULT', {
    result,
    failure_reason: failureReason,
    order_id: String(order.order_id || order.id || pending.order_id || ''),
    order_no: String(order.order_no || pending.order_no || ''),
    payment_channel: pending.payment_channel || 'unknown',
    amount: Number(order.total_fee || pending.amount || 0),
    order_status: status
  })
  writeJson(window.localStorage, PENDING_PAYMENT_STORAGE_KEY, {
    ...pending,
    reported_result: result,
    reported_at: now()
  })
}
